/**
 * App Bridge initialization for embedded apps
 */
import { createApp } from '@shopify/app-bridge';
import { Redirect } from '@shopify/app-bridge/actions';

// Get config from URL params
const urlParams = new URLSearchParams(window.location.search);
const shop = urlParams.get('shop');
const host = urlParams.get('host');

let appBridgeApp: ReturnType<typeof createApp> | null = null;

/**
 * Initialize App Bridge if in embedded context
 */
export function initAppBridge() {
  // Initialize if we have host (embedded context)
  // Shop can be derived from host if not in URL
  if (host && typeof window !== 'undefined') {
    // Try to get API key from Vite env, or from window if injected
    let apiKey = import.meta.env.VITE_SHOPIFY_API_KEY || '';
    
    // If not in Vite env, check if App Bridge is already available
    // Shopify may auto-inject it, so we can use that
    if (!apiKey && typeof window !== 'undefined' && (window as any).shopify?.appBridge) {
      console.log('[App Bridge] Using Shopify-injected App Bridge');
      return (window as any).shopify.appBridge;
    }
    
    // Try to get from meta tag or data attribute
    if (!apiKey) {
      const metaTag = document.querySelector('meta[name="shopify-api-key"]');
      apiKey = metaTag?.getAttribute('content') || '';
    }
    
    if (apiKey) {
      appBridgeApp = createApp({
        apiKey,
        host,
      });
      
      console.log('[App Bridge] Initialized for embedded app with API key');
      return appBridgeApp;
    } else {
      console.warn('[App Bridge] API key not found. Set VITE_SHOPIFY_API_KEY in .env or add meta tag.');
      // Still try to create app - App Bridge CDN might handle it
      try {
        appBridgeApp = createApp({
          apiKey: '', // Will use Shopify's default
          host,
        });
        console.log('[App Bridge] Initialized without explicit API key (may use Shopify default)');
        return appBridgeApp;
      } catch (error) {
        console.error('[App Bridge] Failed to initialize:', error);
      }
    }
  }
  
  return null;
}

/**
 * Get the App Bridge instance
 */
export function getAppBridge() {
  if (!appBridgeApp && host) {
    return initAppBridge();
  }
  return appBridgeApp;
}

/**
 * Check if app is in embedded context
 */
export function isEmbedded(): boolean {
  return !!host || window.self !== window.top;
}

/**
 * Extract shop domain from host parameter
 * The host parameter is base64-encoded JSON containing shop information
 */
export function getShopFromHost(hostParam: string | null): string | null {
  if (!hostParam) {
    return null;
  }

  try {
    // Decode base64 host parameter
    const decoded = atob(hostParam);
    const hostData = JSON.parse(decoded);
    
    // Extract shop from host data
    // Host data format: { shop: "shop-name.myshopify.com", ... }
    if (hostData.shop) {
      return hostData.shop;
    }
  } catch (error) {
    console.warn('[App Bridge] Failed to decode host parameter:', error);
  }

  return null;
}

/**
 * Get shop domain from URL params or host parameter
 * This is the main function to use when you need the shop but it might not be in the URL
 */
export function getShopDomain(): string | null {
  // First try to get from shop parameter
  if (shop) {
    return shop;
  }

  // If not available, try to decode from host parameter
  if (host) {
    const shopFromHost = getShopFromHost(host);
    if (shopFromHost) {
      return shopFromHost;
    }
  }

  return null;
}

/**
 * Navigate to a URL using App Bridge Redirect for embedded apps
 * Falls back to window.location for non-embedded contexts
 * 
 * This is the CORRECT way to handle redirects in embedded Shopify apps.
 * Using window.top.location.href can be blocked by browser security policies.
 * 
 * @param url - The URL to navigate to (relative or absolute)
 * @param path - Optional path for App Bridge Redirect (defaults to extracting from url)
 */
export function navigateTo(url: string, path?: string): void {
  const app = getAppBridge();
  const isEmbeddedContext = isEmbedded();
  
  if (app && isEmbeddedContext) {
    // Use App Bridge Redirect for embedded apps
    // Extract path from URL if not provided
    const redirectPath = path || (url.startsWith('/') ? url : new URL(url, window.location.origin).pathname);
    
    try {
      const redirect = Redirect.create(app);
      redirect.dispatch(Redirect.Action.REMOTE, redirectPath);
      console.log('[App Bridge] Redirecting to:', redirectPath);
    } catch (error) {
      console.error('[App Bridge] Failed to dispatch redirect, falling back to window.location:', error);
      // Fallback to window.location if App Bridge redirect fails
      window.location.href = url;
    }
  } else {
    // Non-embedded context - use standard navigation
    window.location.href = url;
  }
}

/**
 * Navigate to OAuth begin URL with proper return URL handling
 * Uses App Bridge Redirect for embedded apps
 * 
 * @param shopDomain - The shop domain (e.g., "shop.myshopify.com")
 * @param returnPath - Optional return path after OAuth (defaults to /admin)
 */
export function navigateToOAuth(shopDomain: string, returnPath: string = '/admin'): void {
  const app = getAppBridge();
  const isEmbeddedContext = isEmbedded();
  
  // Build return URL with shop parameter
  const returnUrl = `${returnPath}?shop=${encodeURIComponent(shopDomain)}`;
  // Build full URL for OAuth begin (App Bridge REMOTE requires full URL)
  const authPath = `/api/auth/begin?shop=${encodeURIComponent(shopDomain)}&return_url=${encodeURIComponent(returnUrl)}`;
  const authUrl = `${window.location.origin}${authPath}`;
  
  if (app && isEmbeddedContext) {
    // Use App Bridge Redirect for embedded apps
    // REMOTE action requires full URL (including protocol and domain)
    try {
      const redirect = Redirect.create(app);
      redirect.dispatch(Redirect.Action.REMOTE, authUrl);
      console.log('[App Bridge] Redirecting to OAuth (REMOTE):', authUrl);
    } catch (error) {
      console.error('[App Bridge] Failed to dispatch OAuth redirect, falling back to window.location:', error);
      window.location.href = authPath;
    }
  } else {
    // Non-embedded context - use standard navigation
    window.location.href = authPath;
  }
}
